import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  Alert,
  useTheme,
  useMediaQuery,
  Chip,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import Masonry from '@mui/lab/Masonry';
import FilterListIcon from '@mui/icons-material/FilterList';
import { smartFeedAPI } from './smartFeedAPI';
import SmartCard from './SmartCard';
import FilterPanel from './FilterPanel';
import { useSubscription } from './SubscriptionContext';
import { useSelector } from 'react-redux';
import SubscriptionUpgradeModal from './SubscriptionUpgradeModal';
import QuickActions from './QuickActions';
// LAP-733 (GE022): pull `getUserLanguage` from translations so the feed
// follows the i18next active language, not the legacy `user_parameter.Langue`.
import { getTranslation, getUserLanguage } from './translations';
import { useTranslation } from 'react-i18next';

const LOCATION_PROMPT_SESSION_KEY = 'lifeSmartFeedLocationPromptSeen';
const LOCATION_ALLOWED_SESSION_KEY = 'lifeSmartFeedLocationAllowed';
const WEATHER_CACHE_SESSION_KEY = 'lifeSmartFeedWeatherCache';

const getCachedWeather = () => {
  if (typeof window === 'undefined') return null;

  try {
    const cached = JSON.parse(sessionStorage.getItem(WEATHER_CACHE_SESSION_KEY) || 'null');
    if (!cached?.timestamp || Date.now() - cached.timestamp > 10 * 60 * 1000) {
      return null;
    }
    return cached.weather || null;
  } catch (err) {
    return null;
  }
};

const getPublicationDedupeKey = (publication) => {
  if (publication?.define_publication_id) {
    return `definition:${publication.define_publication_id}`;
  }

  return [
    publication?.domain || '',
    publication?.type || '',
    publication?.title || '',
    publication?.button_target || '',
  ].join('|').toLowerCase();
};

const dedupePublications = (items = []) => {
  const seen = new Set();
  const unique = items.filter((publication) => {
    const key = getPublicationDedupeKey(publication);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  // LAP-750: ensure newest posts always appear first
  return unique.sort((a, b) => {
    const ta = a?.datetime_displayed ? new Date(a.datetime_displayed).getTime() : 0;
    const tb = b?.datetime_displayed ? new Date(b.datetime_displayed).getTime() : 0;
    return tb - ta;
  });
};

const clearLocationSessionState = () => {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(LOCATION_PROMPT_SESSION_KEY);
  sessionStorage.removeItem(LOCATION_ALLOWED_SESSION_KEY);
  sessionStorage.removeItem(WEATHER_CACHE_SESSION_KEY);
};

const SmartFeedDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // LAP-733: subscribe to i18next so the dashboard re-renders when the user
  // switches language via the LanguageSelector.
  const { i18n: i18nInstance } = useTranslation();
  const loadMoreRef = useRef(null);
  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  const hasInitialFeedLoadCompletedRef = useRef(false);
  const handleRefreshRef = useRef(null);
  // Guard against concurrent refresh calls that trigger backend 429s.
  // useState's setRefreshing is async, so we need a synchronous ref check.
  const isRefreshingRef = useRef(false);
  const pullStateRef = useRef({ startY: 0, currentY: 0, isDragging: false, pullDistance: 0 });
  const publicationsRef = useRef([]);
  const { subscription, isLoading: subscriptionLoading } = useSubscription();
  const siteUser = useSelector((store) => store.user);
  
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [locationPromptOpen, setLocationPromptOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    const locationAllowed = sessionStorage.getItem(LOCATION_ALLOWED_SESSION_KEY) === 'true';
    return !locationAllowed;
  });
  const [filters, setFilters] = useState({
    domain: 'all',
    type: 'all',
  });
  // LAP-733: weather location strings are now resolved against i18next.
  const initialLanguage = (i18nInstance?.language) || 'fr';
  const [weather, setWeather] = useState(() => {
    const cachedWeather = getCachedWeather();
    if (cachedWeather) return cachedWeather;

    if (typeof window !== 'undefined') {
      if (sessionStorage.getItem(LOCATION_ALLOWED_SESSION_KEY) === 'true') {
        return {
          temperature: null,
          location: getTranslation('locationLoading', initialLanguage),
          weatherCode: null,
        };
      }
    }

    return {
      temperature: null,
      location: getTranslation('locationAllowPrompt', initialLanguage),
      weatherCode: null,
    };
  });
  
  // Pull-to-refresh state
  const [pullToRefresh, setPullToRefresh] = useState({
    isPulling: false,
    pullDistance: 0,
    threshold: 60,
  });
  
  // Pagination state (LimitOffsetPagination)
  // LAP-726 (GE021) — initial batch is 15 cards, next batch triggers at 80%
  // scroll height (handled by the IntersectionObserver further down).
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 15; // Items per page (LAP-726)

  // LAP-726 — "X nouvelles publications" pill: counts cards generated by a
  // refresh while the user has scrolled away from the top of the feed.
  const [newCardsCount, setNewCardsCount] = useState(0);
  const feedScrollRef = useRef(null);

  useEffect(() => {
    publicationsRef.current = publications;
  }, [publications]);

  // LAP-733 (GE022): resolve from i18next first, then user preferences, then
  // legacy fields, then navigator. Defined as a stable reference using the
  // latest i18n language and the live siteUser.
  const resolveLanguage = useCallback(() => getUserLanguage(siteUser), [siteUser]);

  // Convenience getter so existing call sites read naturally.
  const userLanguage = resolveLanguage();
  // Re-resolve whenever i18next.language changes so feed reloads pick it up.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const currentLang = i18nInstance?.language;

  const loadFeed = useCallback(async (currentOffset = 0, isRefresh = false, append = false) => {
    if (!isMountedRef.current) return;
    
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      const language = resolveLanguage();
      let response;

      // LAP-726 (GE021) — `status` argument is kept for backwards compat with
      // older smartFeedAPI signatures but is no longer used by the backend.
      // The feed is a single unified chronological list driven by
      // `was_dismissed=false`.
      response = await smartFeedAPI.getFeed(
        limit,
        currentOffset,
        filters.domain !== 'all' ? filters.domain : null,
        filters.type !== 'all' ? filters.type : null,
        subscription,
        language,
        null
      );
      
      if (!isMountedRef.current) return;
      
      // LimitOffsetPagination response format: { count, next, previous, results }
      if (response && response.results) {
        if (append) {
          // Append new publications to existing ones
          setPublications(prev => {
            const existingIds = new Set(prev.map(pub => pub.id));
            return dedupePublications([
              ...prev,
              ...response.results.filter(pub => !existingIds.has(pub.id)),
            ]);
          });
        } else {
          // Replace publications (initial load or refresh)
          setPublications(dedupePublications(response.results));
        }
        
        // Update pagination state
        setOffset(currentOffset);
        setTotalCount(response.count || response.results.length);
        setHasMore(response.next !== null);
      }
      
      setError(null);
      return response;
    } catch (err) {
      if (!isMountedRef.current) return;
      if (err.message === 'SUBSCRIPTION_REQUIRED') {
        setError(getTranslation('subscriptionRequired', resolveLanguage()));
      } else {
        setError(getTranslation('loadError', resolveLanguage()));
      }
      console.error('Error loading feed:', err);
      return null;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setRefreshing(false);
        setLoadingMore(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, filters.domain, filters.type, subscription, siteUser, currentLang]);

  // Load more publications (infinite scroll)
  const loadMorePublications = useCallback(() => {
    if (!loadingMore && hasMore && !loading && !refreshing) {
      const nextOffset = offset + limit;
      loadFeed(nextOffset, false, true);
    }
  }, [loadingMore, hasMore, loading, refreshing, offset, limit, loadFeed]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Initial feed load - wait for subscription to be ready
  const hasAutoRefreshedRef = useRef(false);
  
  useEffect(() => {
    let isMounted = true;
    
    const initialLoad = async () => {
      // Only load once per component mount
      if (hasAutoRefreshedRef.current) {
        return;
      }
      
      // Wait for subscription to be loaded
      if (subscriptionLoading) {
        return; // Will retry when subscriptionLoading becomes false
      }
      
      if (!isMounted) return;
      
      // Mark as auto-refreshed to prevent multiple calls
      hasAutoRefreshedRef.current = true;
      
      try {
        const response = await loadFeed(0, false, false);
        const hasExistingFeed = response?.results?.length > 0;
        
        // Generate cards only when there is nothing ready to display.
        // This keeps the page fast and avoids showing one feed before replacing it.
        if (!hasExistingFeed && isMounted) {
          setRefreshing(true);
          const language = resolveLanguage();
          await smartFeedAPI.refreshFeed(subscription, language);
          setOffset(0);
          setHasMore(true);
          await loadFeed(0, true, false);
        }
      } catch (err) {
        // Silently handle errors during auto-refresh - just load existing feed
        // Don't show errors to user for auto-refresh failures
        if (isMounted) {
          // For any error (429, network, etc.), just load the existing feed without refresh
          try {
            await loadFeed();
          } catch (loadError) {
            // If loading feed also fails, only then show error
            console.error('Error loading feed after auto-refresh failure:', loadError);
            if (isMounted) {
              setError(getTranslation('loadError', resolveLanguage()));
            }
          }
        }
      } finally {
        if (isMounted) {
          hasInitialFeedLoadCompletedRef.current = true;
          setRefreshing(false);
        }
      }
    };
    
    initialLoad();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionLoading, subscription]); // Run when subscription is loaded or changes

  // Reload when filters or subscription change (but not on initial mount)
  useEffect(() => {
    if (!hasInitialFeedLoadCompletedRef.current) {
      return;
    }

    if (!subscriptionLoading && subscription && isMountedRef.current) {
      setLoading(true);
      loadFeed(0, false, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.domain, filters.type, subscription, subscriptionLoading]);

  // Refetch when user returns to tab (visibility change) without replacing the feed already on screen.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isMountedRef.current && !subscriptionLoading) {
        handleRefreshRef.current?.();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [subscriptionLoading]);

  const getCurrentPosition = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('GEOLOCATION_NOT_SUPPORTED'));
      return;
    }

    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: false,
      timeout: 10000,
      maximumAge: 10 * 60 * 1000,
    });
  });

  const fetchJson = async (url) => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP_${response.status}`);
    }
    return response.json();
  };

  const getLocationName = async (latitude, longitude) => {
    try {
      const locationData = await fetchJson(
        `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=${resolveLanguage()}`
      );

      const city = locationData.address?.city ||
        locationData.address?.town ||
        locationData.address?.village ||
        locationData.address?.municipality ||
        locationData.address?.county ||
        locationData.name;
      const country = locationData.address?.country || '';

      if (city && country) return `${city}, ${country}`;
      if (city) return city;
      if (country) return country;
    } catch (err) {
      console.error('Error fetching location name:', err);
    }

    return getTranslation('locationCurrent', resolveLanguage());
  };

  const getWeatherForCoordinates = async (latitude, longitude, fallbackLocationName = null) => {
    const weatherData = await fetchJson(
      `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code&current_weather=true`
    );
    const currentWeather = weatherData.current_weather || {
      temperature: weatherData.current?.temperature_2m,
      weathercode: weatherData.current?.weather_code,
    };

    if (currentWeather.temperature === undefined || currentWeather.weathercode === undefined) {
      throw new Error('WEATHER_DATA_UNAVAILABLE');
    }

    const locationName = fallbackLocationName || await getLocationName(latitude, longitude);

    return {
      temperature: Math.round(currentWeather.temperature),
      location: locationName,
      weatherCode: currentWeather.weathercode,
    };
  };

  const closeLocationPromptForSession = () => {
    sessionStorage.setItem(LOCATION_PROMPT_SESSION_KEY, 'true');
    sessionStorage.removeItem(LOCATION_ALLOWED_SESSION_KEY);
    setLocationPromptOpen(false);
    setWeather(prev => (
      prev.temperature === null
        ? {
            temperature: null,
            location: getTranslation('locationAllowPrompt', resolveLanguage()),
            weatherCode: null,
          }
        : prev
    ));
  };

  const fetchWeather = async ({ rememberChoice = true } = {}) => {
    if (rememberChoice) {
      sessionStorage.setItem(LOCATION_PROMPT_SESSION_KEY, 'true');
    }
    setLocationPromptOpen(false);
    try {
      const position = await getCurrentPosition();
      if (!isMountedRef.current) return;

      const { latitude, longitude } = position.coords;
      const weatherResult = await getWeatherForCoordinates(latitude, longitude);

      if (!isMountedRef.current) return;
      sessionStorage.setItem(LOCATION_ALLOWED_SESSION_KEY, 'true');
      sessionStorage.setItem(WEATHER_CACHE_SESSION_KEY, JSON.stringify({
        timestamp: Date.now(),
        weather: weatherResult,
      }));
      setWeather(weatherResult);
    } catch (err) {
      console.error('Error fetching weather:', err);
      if (!isMountedRef.current) return;
      clearLocationSessionState();
      const isPermissionDenied = err.code === 1 || err.message === 'GEOLOCATION_NOT_SUPPORTED';
      setWeather({
        temperature: null,
        location: getTranslation(
          isPermissionDenied ? 'locationDenied' : 'locationUnavailable',
          resolveLanguage()
        ),
        weatherCode: null,
      });
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hasAllowedLocation = sessionStorage.getItem(LOCATION_ALLOWED_SESSION_KEY) === 'true';
    const hasSeenPrompt = sessionStorage.getItem(LOCATION_PROMPT_SESSION_KEY) === 'true';

    if (hasAllowedLocation) {
      fetchWeather({ rememberChoice: false });
      return;
    }

    if (hasSeenPrompt) {
      clearLocationSessionState();
      setLocationPromptOpen(true);
      setWeather({
        temperature: null,
        location: getTranslation('locationAllowPrompt', resolveLanguage()),
        weatherCode: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Infinite scroll using Intersection Observer.
  //
  // LAP-726 (GE021): "Load next batch when user reaches 80% of current list
  // height". We achieve that by placing the sentinel at the *bottom* of the
  // list and using a rootMargin equal to 20% of the viewport height — i.e.
  // the IntersectionObserver fires once the sentinel is within the last 20%
  // of the viewport, which corresponds to the user having scrolled past 80%
  // of the current list.
  useEffect(() => {
    if (!loadMoreRef.current || !isMountedRef.current) return;

    const triggerMargin = Math.round(window.innerHeight * 0.2);

    const observer = new IntersectionObserver(
      (entries) => {
        if (!isMountedRef.current) return;
        const target = entries[0];
        if (target.isIntersecting && !loadingMore && hasMore && !loading && !refreshing) {
          loadMorePublications();
        }
      },
      {
        root: null,
        rootMargin: `0px 0px ${triggerMargin}px 0px`,
        threshold: 0,
      }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef && isMountedRef.current) {
      try {
        observer.observe(currentRef);
      } catch (err) {
        console.error('Error observing element:', err);
      }
    }

    return () => {
      if (currentRef && currentRef.parentNode) {
        try {
          observer.unobserve(currentRef);
        } catch (err) {
          // Element might already be removed, ignore
        }
      }
      try {
        observer.disconnect();
      } catch (err) {
        // Observer might already be disconnected, ignore
      }
    };
  }, [loadMorePublications, loadingMore, hasMore, loading, refreshing]);

  // Container ref for pull-to-refresh
  const containerRef = useRef(null);

  // Pull-to-refresh functionality
  useEffect(() => {
    const threshold = 60; // Lower threshold for easier triggering

    const checkScrollTop = () => {
      // Check window scroll position
      const windowScroll = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      // Also check if document body can scroll
      const canScroll = document.documentElement.scrollHeight > window.innerHeight;
      // If content doesn't fill viewport, always allow pull-to-refresh
      if (!canScroll) {
        return true;
      }
      // If content can scroll, check if we're at the top (with tolerance)
      const isAtTop = windowScroll <= 20; // Increased tolerance
      console.log('checkScrollTop:', { windowScroll, canScroll, isAtTop });
      return isAtTop;
    };

    const handleTouchStart = (e) => {
      const atTop = checkScrollTop();
      console.log('TouchStart:', { atTop, refreshing, touches: e.touches.length });
      
      // Only trigger if at the top of the page and not already refreshing
      if (atTop && !refreshing) {
        pullStateRef.current.startY = e.touches[0].clientY;
        pullStateRef.current.isDragging = true;
        pullStateRef.current.pullDistance = 0;
        console.log('✅ Pull-to-refresh: Touch started at top, startY:', pullStateRef.current.startY);
      } else {
        console.log('❌ Pull-to-refresh: Not starting - atTop:', atTop, 'refreshing:', refreshing);
      }
    };

    const handleTouchMove = (e) => {
      const state = pullStateRef.current;
      
      if (!state.isDragging) {
        return;
      }
      
      // Check if we're still at the top (allow some tolerance)
      const stillAtTop = checkScrollTop();
      
      if (!stillAtTop) {
        // User scrolled away from top, cancel pull
        console.log('❌ Scrolled away from top, canceling pull');
        state.isDragging = false;
        setPullToRefresh({
          isPulling: false,
          pullDistance: 0,
          threshold: 60,
        });
        return;
      }

      state.currentY = e.touches[0].clientY;
      state.pullDistance = state.currentY - state.startY;

      console.log('TouchMove:', { 
        currentY: state.currentY, 
        startY: state.startY, 
        pullDistance: state.pullDistance,
        threshold 
      });

      if (state.pullDistance > 0) {
        e.preventDefault();
        e.stopPropagation();
        setPullToRefresh(prev => ({
          ...prev,
          isPulling: true,
          pullDistance: Math.min(state.pullDistance, threshold * 1.5),
        }));
      } else {
        // Pulling up, not down - reset
        setPullToRefresh({
          isPulling: false,
          pullDistance: 0,
          threshold: 60,
        });
      }
    };

    const handleTouchEnd = () => {
      const state = pullStateRef.current;
      if (!state.isDragging) {
        console.log('TouchEnd: Not dragging, ignoring');
        return;
      }

      console.log('✅ Pull-to-refresh: Touch end - distance:', state.pullDistance, 'threshold:', threshold, 'handleRefreshRef exists:', !!handleRefreshRef.current);

      if (state.pullDistance >= threshold) {
        // Trigger refresh using ref to get latest version
        console.log('🚀 Pull-to-refresh: Triggering refresh! Distance:', state.pullDistance);
        if (handleRefreshRef.current) {
          handleRefreshRef.current();
        } else {
          console.error('❌ handleRefreshRef.current is null! Cannot refresh.');
        }
      } else {
        console.log('⚠️ Pull distance not enough:', state.pullDistance, '<', threshold);
      }

      // Reset pull state
      setPullToRefresh({
        isPulling: false,
        pullDistance: 0,
        threshold: 60,
      });
      
      state.isDragging = false;
      state.pullDistance = 0;
    };

    // Mouse events for desktop testing
    const handleMouseDown = (e) => {
      const atTop = checkScrollTop();
      console.log('MouseDown:', { atTop, refreshing, button: e.button });
      
      if (atTop && e.button === 0 && !refreshing) {
        pullStateRef.current.startY = e.clientY;
        pullStateRef.current.isDragging = true;
        pullStateRef.current.pullDistance = 0;
        console.log('✅ Pull-to-refresh: Mouse down at top, startY:', pullStateRef.current.startY);
      }
    };

    const handleMouseMove = (e) => {
      const state = pullStateRef.current;
      
      if (!state.isDragging) return;
      
      // Check if we're still at the top
      const stillAtTop = checkScrollTop();
      
      if (!stillAtTop) {
        // User scrolled away from top, cancel pull
        console.log(' Mouse moved away from top, canceling pull');
        state.isDragging = false;
        setPullToRefresh({
          isPulling: false,
          pullDistance: 0,
          threshold: 50,
        });
        return;
      }

      state.currentY = e.clientY;
      state.pullDistance = state.currentY - state.startY;

      console.log('MouseMove:', { 
        currentY: state.currentY, 
        startY: state.startY, 
        pullDistance: state.pullDistance,
        threshold 
      });

      if (state.pullDistance > 0) {
        setPullToRefresh(prev => ({
          ...prev,
          isPulling: true,
          pullDistance: Math.min(state.pullDistance, threshold * 1.5),
        }));
      } else {
        // Pulling up, not down - reset
        setPullToRefresh({
          isPulling: false,
          pullDistance: 0,
          threshold: 60,
        });
      }
    };

    const handleMouseUp = () => {
      const state = pullStateRef.current;
      if (!state.isDragging) {
        console.log('MouseUp: Not dragging, ignoring');
        return;
      }

      console.log(' Pull-to-refresh: Mouse up - distance:', state.pullDistance, 'threshold:', threshold, 'handleRefreshRef exists:', !!handleRefreshRef.current);

      if (state.pullDistance >= threshold) {
        console.log(' Pull-to-refresh: Triggering refresh (mouse)! Distance:', state.pullDistance);
        if (handleRefreshRef.current) {
          handleRefreshRef.current();
        } else {
          console.error(' handleRefreshRef.current is null! Cannot refresh.');
        }
      } else {
        console.log(' Pull distance not enough:', state.pullDistance, '<', threshold);
      }

      setPullToRefresh({
        isPulling: false,
        pullDistance: 0,
        threshold: 60,
      });
      
      state.isDragging = false;
      state.pullDistance = 0;
    };

    // Also listen for scroll events to enable pull-to-refresh when user scrolls to top
    const handleScroll = () => {
      const state = pullStateRef.current;
      if (!checkScrollTop() && state.isDragging) {
        state.isDragging = false;
        setPullToRefresh({
          isPulling: false,
          pullDistance: 0,
          threshold: 60,
        });
      }
    };

    // Attach events to document body for better capture
    const documentBody = document.body;
    
    console.log('📌 Setting up pull-to-refresh event listeners');
    
    documentBody.addEventListener('touchstart', handleTouchStart, { passive: false });
    documentBody.addEventListener('touchmove', handleTouchMove, { passive: false });
    documentBody.addEventListener('touchend', handleTouchEnd, { passive: true });
    window.addEventListener('mousedown', handleMouseDown, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('mouseup', handleMouseUp, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      documentBody.removeEventListener('touchstart', handleTouchStart);
      documentBody.removeEventListener('touchmove', handleTouchMove);
      documentBody.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Listeners attached once - handleRefresh accessed via handleRefreshRef

  const handleRefresh = useCallback(async () => {
    console.log('🔄 handleRefresh called');
    if (!isMountedRef.current) return;
    // Block concurrent invocations: pull-to-refresh handlers can fire several
    // times in rapid succession (touchend + mouseup + scroll), which spams
    // /refresh/ and trips the backend 429 rate-limiter.
    if (isRefreshingRef.current) {
      console.log('⏭️ Refresh already in progress, skipping');
      return;
    }
    isRefreshingRef.current = true;

    try {
      setRefreshing(true);
      const language = resolveLanguage();
      console.log('🔄 Calling refreshFeed API...');
      const response = await smartFeedAPI.refreshFeed(subscription, language);
      console.log('✅ Refresh API response:', response);
      
      if (!isMountedRef.current) return;
      
      setHasMore(true);
      const newPublications = response?.publications || [];
      if (newPublications.length > 0) {
        const existingIds = new Set(publicationsRef.current.map(pub => pub.id));
        const uniqueNewPublications = newPublications.filter(pub => !existingIds.has(pub.id));
        setPublications(prev => {
          const currentIds = new Set(prev.map(pub => pub.id));
          return [
            ...uniqueNewPublications.filter(pub => !currentIds.has(pub.id)),
            ...prev,
          ];
        });
        setTotalCount(prev => prev + uniqueNewPublications.length);
        setOffset(prev => prev + uniqueNewPublications.length);

        // LAP-726 (GE021) — if the user is not at the top, surface the "X
        // nouvelles publications" pill so they can jump to the new cards.
        const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
        if (scrollY > 200) {
          setNewCardsCount(prev => prev + uniqueNewPublications.length);
        }
      } else {
        await loadFeed(0, true, false);
      }
      setRefreshing(false);
    } catch (err) {
      console.error('❌ Error refreshing feed:', err);
      if (!isMountedRef.current) return;
      if (err.message === 'SUBSCRIPTION_REQUIRED') {
        setError(getTranslation('subscriptionRequired', resolveLanguage()));
      } else if (err.message === 'Failed to fetch' || err.code === 'ERR_NETWORK' || err.message === 'NETWORK_ERROR') {
        setError(getTranslation('networkError', resolveLanguage()));
      } else if (err.response?.status === 429) {
        // Backend rate-limiter hit. Show a dedicated message so the user
        // understands the refresh failed because of throttling, not a bug.
        setError(getTranslation('rateLimitError', resolveLanguage()));
      } else {
        setError(getTranslation('refreshError', resolveLanguage()));
      }
      setRefreshing(false);
    } finally {
      // Always release the lock, even if the request failed.
      isRefreshingRef.current = false;
    }
  }, [subscription, loadFeed, resolveLanguage]);

  // Update handleRefresh ref when handleRefresh changes
  useEffect(() => {
    handleRefreshRef.current = handleRefresh;
  }, [handleRefresh]);

  const handleInteraction = async (publicationId, action) => {
    // LAP-726 (GE021) — Dismiss must feel instant. We remove the card
    // optimistically before the API call resolves and re-insert it on error
    // so the user never sees a half-second delay tap → fade-out.
    let snapshot = null;
    if (action === 'dismiss' || action === 'snooze') {
      snapshot = publicationsRef.current;
      setPublications(prev => prev.filter(pub => pub.id !== publicationId));
    }

    try {
      const response = await smartFeedAPI.interact(publicationId, action);

      if (response.success) {
        // Dismiss / snooze already removed optimistically — nothing else to do.
        if (action === 'dismiss' || action === 'snooze') {
          return;
        }
        // Other actions (like, save, click, ...) — update the card in place.
        setPublications(prev =>
          prev.map(pub =>
            pub.id === publicationId ? response.publication : pub
          )
        );
      }
    } catch (err) {
      console.error('Error handling interaction:', err);
      // LAP-726 — restore the optimistic dismiss/snooze on failure so the
      // card doesn't silently vanish if the API call fails.
      if (snapshot) {
        setPublications(snapshot);
      }
      setError(getTranslation('refreshError', resolveLanguage()));
    }
  };

  const getWeatherIcon = (weatherCode) => {
    // WMO Weather interpretation codes (WW)
    // https://open-meteo.com/en/docs
    if (weatherCode === null) return '🌡️';
    if (weatherCode === 0) return '☀️'; // Clear sky
    if (weatherCode >= 1 && weatherCode <= 3) return '⛅'; // Mainly clear, partly cloudy, and overcast
    if (weatherCode >= 45 && weatherCode <= 48) return '🌫️'; // Fog
    if (weatherCode >= 51 && weatherCode <= 57) return '🌧️'; // Drizzle
    if (weatherCode >= 61 && weatherCode <= 67) return '🌧️'; // Rain
    if (weatherCode >= 71 && weatherCode <= 77) return '❄️'; // Snow
    if (weatherCode >= 80 && weatherCode <= 82) return '🌦️'; // Rain showers
    if (weatherCode >= 85 && weatherCode <= 86) return '🌨️'; // Snow showers
    if (weatherCode >= 95 && weatherCode <= 99) return '⛈️'; // Thunderstorm
    return '🌡️'; // Default
  };

  const filteredPublications = publications.filter(pub => {
    if (filters.domain !== 'all' && pub.domain !== filters.domain) return false;
    if (filters.type !== 'all' && pub.type !== filters.type) return false;
    return true;
  });

  if (loading && publications.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
        bgcolor="#0b0e1a"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        minHeight: '100vh',
        bgcolor: '#0b0e1a',
        color: '#E0E0E0',
        fontFamily: '"Inter", "Roboto", sans-serif',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* LAP-726 (GE021) — "X nouvelles publications" pill. Shown when the
            user has scrolled away from the top and a refresh has produced new
            cards. Tapping it scrolls back to the top and resets the count. */}
        {newCardsCount > 0 && (
          <Box
            role="button"
            tabIndex={0}
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
              setNewCardsCount(0);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setNewCardsCount(0);
              }
            }}
            sx={{
              position: 'fixed',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1400,
              px: 2,
              py: 1,
              borderRadius: 999,
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.875rem',
              boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
            }}
          >
            {getTranslation('newPublicationsPill', userLanguage, { count: newCardsCount })}
          </Box>
        )}
        <Dialog
          open={locationPromptOpen}
          onClose={closeLocationPromptForSession}
          PaperProps={{
            sx: {
              bgcolor: '#1a1d2e',
              color: '#E0E0E0',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <DialogTitle>{getTranslation('locationAllowTitle', userLanguage)}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.75)' }}>
              {getTranslation('locationAllowBody', userLanguage)}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={closeLocationPromptForSession}
              sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              {getTranslation('later', userLanguage)}
            </Button>
            <Button onClick={() => fetchWeather()} variant="contained">
              {getTranslation('allow', userLanguage)}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <Box>
            <Box
              display="flex"
              alignItems="center"
              gap={1.5}
              mb={1}
              role="button"
              tabIndex={0}
              onClick={() => {
                if (weather.temperature === null) {
                  setLocationPromptOpen(true);
                }
              }}
              onKeyDown={(event) => {
                if (weather.temperature === null && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  setLocationPromptOpen(true);
                }
              }}
              sx={{ cursor: weather.temperature === null ? 'pointer' : 'default' }}
            >
              {/* Weather Icon */}
              <Typography
                variant="h3"
                component="span"
                sx={{
                  fontSize: '2.5rem',
                  lineHeight: 1,
                }}
              >
                {getWeatherIcon(weather.weatherCode)}
              </Typography>
              
              {/* Temperature */}
              {weather.temperature !== null && (
                <Typography
                  variant="h3"
                  component="h1"
                  fontWeight="bold"
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {weather.temperature}°C
                </Typography>
              )}
              
              {/* Location */}
              <Typography
                variant="h5"
                component="span"
                sx={{
                  color: '#E0E0E0',
                  fontWeight: 500,
                }}
              >
                {weather.location}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {getTranslation('publicationsCount', userLanguage, {
                  shown: filteredPublications.length,
                  total: totalCount,
                })}
              </Typography>
              {subscription && (
                <Chip
                  label={subscription}
                  size="small"
                  sx={{
                    bgcolor: subscription === 'PREMIUM' 
                      ? alpha('#8b5cf6', 0.2) 
                      : subscription === 'STANDARD'
                      ? alpha('#3b82f6', 0.2)
                      : alpha('#6366f1', 0.2),
                    color: subscription === 'PREMIUM' 
                      ? '#8b5cf6' 
                      : subscription === 'STANDARD'
                      ? '#3b82f6'
                      : '#6366f1',
                    fontSize: '0.7rem',
                    height: '20px',
                  }}
                />
              )}
            </Box>
          </Box>

          <Box display="flex" gap={1} alignItems="center">
            <IconButton
              onClick={() => setFilterOpen(!filterOpen)}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <FilterListIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filter Panel - Always render to prevent portal unmount errors */}
        <FilterPanel
          filters={filters}
          onFilterChange={setFilters}
          publications={publications}
          open={filterOpen}
        />

        {/* Pull-to-refresh indicator */}
        {pullToRefresh.isPulling && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              height: `${Math.min(pullToRefresh.pullDistance, pullToRefresh.threshold * 1.5)}px`,
              bgcolor: 'rgba(102, 126, 234, 0.1)',
              zIndex: 1000,
              transform: `translateY(${pullToRefresh.pullDistance > pullToRefresh.threshold ? 0 : -100}%)`,
              transition: pullToRefresh.pullDistance > pullToRefresh.threshold ? 'none' : 'transform 0.3s',
            }}
          >
            <CircularProgress 
              size={24} 
              sx={{ 
                color: pullToRefresh.pullDistance >= pullToRefresh.threshold ? '#667eea' : '#666',
              }} 
            />
            <Typography variant="body2" sx={{ ml: 1, color: '#E0E0E0' }}>
              {pullToRefresh.pullDistance >= pullToRefresh.threshold
                ? getTranslation('releaseToRefresh', userLanguage)
                : getTranslation('pullToRefresh', userLanguage)}
            </Typography>
          </Box>
        )}

        {/* Loading Indicator */}
        {refreshing && (
          <Box display="flex" justifyContent="center" mb={3}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* Publications Masonry Grid */}
        <Box sx={{ position: 'relative' }}>
          <Masonry
            columns={{ xs: 1, sm: 2, md: 3, lg: 4 }}
            spacing={2}
            sx={{
              alignContent: 'flex-start',
            }}
          >
            {filteredPublications.map((publication) => (
              <Box key={publication.id}>
                <SmartCard
                  publication={publication}
                  onInteraction={handleInteraction}
                  onUpgradeRequest={() => setUpgradeModalOpen(true)}
                  showArchivedIndicators={false}
                />
              </Box>
            ))}
          </Masonry>

        </Box>

        {/* Intersection Observer Target - Outside Masonry to prevent ref conflicts */}
        {hasMore && (
          <Box
            ref={loadMoreRef}
            sx={{ 
              height: '20px', 
              margin: '20px 0',
              width: '100%',
            }}
          />
        )}

        {/* Loading More Indicator */}
        {loadingMore && (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              {getTranslation('loadingMore', userLanguage)}
            </Typography>
          </Box>
        )}

        {/* End of List Message */}
        {!hasMore && filteredPublications.length > 0 && !loading && (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              {getTranslation('feedEnd', userLanguage)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {getTranslation('publicationsShown', userLanguage, {
                count: filteredPublications.length,
              })}
            </Typography>
          </Box>
        )}

        {/* Empty State */}
        {filteredPublications.length === 0 && !loading && (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {getTranslation('noPublicationsYet', userLanguage)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {getTranslation('pullToRefreshHint', userLanguage)}
            </Typography>
          </Box>
        )}
      </Container>

      {/* Quick Actions - Mobile */}
      {isMobile && (
        <QuickActions currentDomain={filters.domain !== 'all' ? filters.domain : null} />
      )}


      {/* Subscription Upgrade Modal - Always render to prevent portal unmount errors */}
      <SubscriptionUpgradeModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        onUpgrade={(tier) => {
          // Handle upgrade - could navigate to subscription page or call API
          console.log('Upgrading to:', tier);
          setUpgradeModalOpen(false);
          // Navigate to subscription management or call upgrade API
        }}
      />
    </Box>
  );
};

export default SmartFeedDashboard;
